import {Box} from '@dropbox/dig-foundations';
import {railWidthAtoms} from 'atoms/layout';
import cx from 'classnames';
import {SplitDivider} from 'components/shared/SplitDivider';
import {useMobile} from 'hooks/isMobile';
import {useAtom} from 'jotai';
import {
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type UIEventHandler,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {useLocation, useResolvedPath} from 'react-router';

import {Breadcrumb, BreadcrumbLinkProps} from './Breadcrumb';
import styles from './Layout.module.css';
import {
  DSYS_CONTENT_MAX_OFFSET_PX,
  DSYS_NAVBAR_HEIGHT,
  DSYS_PAGE_GUTTER_PX,
  DSYS_PAGE_HEADER_PADDING_BOTTOM_PX,
  DSYS_PAGE_PADDING_TOP_PX,
  DSYS_RAIL_INSET_LEFT_PX,
  DSYS_RAIL_INSET_RIGHT_PX,
  DSYS_RAIL_ROW_INSET_PX,
  DSYS_RAIL_WIDTH,
  type DsysRailSize,
} from './layoutConstants';
import {Link, LinkProps} from './Link';
import {Title} from './Title';

const clampRailWidth = (width: number) =>
  Math.min(DSYS_RAIL_WIDTH.max, Math.max(DSYS_RAIL_WIDTH.min, width));

type PageHeaderProps = (
  | {title: string; titleTo?: LinkProps['to']; breadcrumb?: never}
  | {breadcrumb: BreadcrumbLinkProps[]; title?: never; titleTo?: never}
) & {
  // Inline right after the title: a filter chip, the narrow screens' nav sheet.
  titleAccessory?: ReactNode;
  // The page's create button, a quiet "+" right after the title (and its
  // accessory): it adds to this section, and stays put however wide the screen.
  create?: ReactNode;
  // An optional second line: tabs, mode switches, pickers.
  toolbar?: ReactNode;
};

/**
 * The page's header, wherever it sits (Layout.Page's spanning bar, the rail's
 * top, or the pane's top). Pages fill its slots; its sizes and spacing are fixed
 * so the first line reads the same on every page. The first line is a title or
 * a breadcrumb (whose first crumb is the section), never both.
 */
const trimTrailingSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

export const PageHeader = ({
  title,
  titleTo,
  breadcrumb,
  titleAccessory,
  create,
  toolbar,
}: PageHeaderProps) => {
  const isMobile = useMobile();
  const titleNode = title !== undefined && <Title size={18}>{title}</Title>;
  // Already at the title's home (the page it links to): a plain title, since the
  // link would go nowhere.
  const location = useLocation();
  const titleTarget = useResolvedPath(titleTo ?? '.');
  const atTitleHome =
    trimTrailingSlash(titleTarget.pathname) === trimTrailingSlash(location.pathname) &&
    titleTarget.search === location.search;

  return (
    <Box display="flex" flexDirection="column" style={{gap: 12}}>
      <Box display="flex" alignItems="center" style={{gap: 4, minHeight: 36}}>
        {breadcrumb ? (
          <Breadcrumb path={breadcrumb} withMarginBottom={false} />
        ) : (
          <Box flexShrink={0}>
            {titleTo && !atTitleHome ? (
              <Link to={titleTo} hasNoUnderline showUnderlineOnHover monochromatic>
                {titleNode}
              </Link>
            ) : (
              titleNode
            )}
          </Box>
        )}
        {titleAccessory && (
          <Box display="flex" alignItems="center" style={{minWidth: 0}}>
            {titleAccessory}
          </Box>
        )}
        {create && (
          <Box display="flex" alignItems="center" flexShrink={0}>
            {create}
          </Box>
        )}
      </Box>
      {toolbar && (
        <Box
          display="flex"
          alignItems="center"
          style={{gap: 8, minHeight: 36, flexWrap: isMobile ? 'wrap' : undefined}}
        >
          {toolbar}
        </Box>
      )}
    </Box>
  );
};

/**
 * A page: an optional header bar spanning the width, then an optional
 * resizable rail beside the pane. All spacing comes from layoutConstants, so
 * titles, rails and content land in the same spot on every page. The pane
 * (children) owns its scrolling and padding; `MaxWidth` caps it.
 */
export const Page = ({
  header,
  rail,
  railSize = 'nav',
  railHeader,
  children,
}: {
  header?: ReactNode;
  // Undefined drops the rail (narrow screens, full-page panes).
  rail?: ReactNode;
  // Which shared width the rail starts at and saves to.
  railSize?: DsysRailSize;
  // The rail's own header, when there's no spanning one.
  railHeader?: ReactNode;
  children: ReactNode;
}) => {
  const [storedRailWidth, setRailWidth] = useAtom(railWidthAtoms[railSize]);
  const railWidth = clampRailWidth(storedRailWidth);
  const bodyRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [bodyTop, setBodyTop] = useState(52);

  // Where the body starts, for panes whose sticky / fixed parts sit under the header.
  useLayoutEffect(() => {
    const measure = () => {
      const top = bodyRef.current?.getBoundingClientRect().top;
      if (typeof top === 'number') setBodyTop(top);
    };
    measure();
    window.addEventListener('resize', measure);
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    if (headerRef.current) observer?.observe(headerRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      className={styles.page}
      style={
        {
          '--dsys-navbar-height': DSYS_NAVBAR_HEIGHT,
          '--dsys-page-padding-top': `${DSYS_PAGE_PADDING_TOP_PX}px`,
          '--dsys-page-gutter': `${DSYS_PAGE_GUTTER_PX}px`,
          '--dsys-content-max-offset': `${DSYS_CONTENT_MAX_OFFSET_PX}px`,
          '--dsys-page-header-padding-bottom': `${DSYS_PAGE_HEADER_PADDING_BOTTOM_PX}px`,
          '--dsys-rail-inset-left': `${DSYS_RAIL_INSET_LEFT_PX}px`,
          '--dsys-rail-inset-right': `${DSYS_RAIL_INSET_RIGHT_PX}px`,
          '--dsys-rail-row-inset': `${DSYS_RAIL_ROW_INSET_PX}px`,
          '--dsys-rail-width': `${railWidth}px`,
          '--dsys-page-body-top': `${bodyTop}px`,
        } as CSSProperties
      }
    >
      {header && (
        <div ref={headerRef} className={styles.pageHeaderBar}>
          {header}
        </div>
      )}
      <div ref={bodyRef} className={styles.pageBody}>
        {rail && (
          <>
            {/* data-page-rail: lets rail content find its scroller (e.g. to reset it). */}
            <div className={styles.pageRail} data-page-rail>
              {!header && railHeader && <div className={styles.pageRailHeader}>{railHeader}</div>}
              {rail}
            </div>
            <SplitDivider
              containerRef={bodyRef}
              width={railWidth}
              minWidth={DSYS_RAIL_WIDTH.min}
              maxWidth={DSYS_RAIL_WIDTH.max}
              onWidthChange={setRailWidth}
              lineAlign="start"
            />
          </>
        )}
        {children}
      </div>
    </div>
  );
};

/**
 * Caps content: `reading` for text columns, `wide` for tables and cards.
 * `inset` adds the page gutter on both sides; `lean` (with `inset`, for
 * editorial panes like News) also leans the content toward the middle on wide
 * panes, up to DSYS_CONTENT_MAX_OFFSET_PX past the gutter. `indent` (with `inset`) pushes the
 * content further right by that many px and takes it off the cap (here and in
 * nested MaxWidths), so the right edge stays where unindented content ends.
 * `breakout` (with `inset`, placed directly in the pane) lets descendants with
 * `breakoutWideClassName` grow evenly past this cap on both sides.
 */
export const MaxWidth = ({
  width,
  inset,
  lean,
  indent,
  breakout,
  className,
  style,
  children,
}: {
  width: 'reading' | 'wide';
  inset?: boolean;
  lean?: boolean;
  indent?: number;
  breakout?: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) => {
  const capped = (
    <div
      className={cx(
        width === 'reading' ? styles.maxWidthReading : styles.maxWidthWide,
        !inset && className
      )}
      style={inset ? undefined : style}
    >
      {children}
    </div>
  );
  if (!inset) return capped;
  const insetArea = (
    <div
      className={cx(
        styles.pageInset,
        width === 'wide' && styles.pageInsetWide,
        lean && styles.pageInsetLean,
        className
      )}
      style={indent ? ({...style, '--dsys-content-indent': `${indent}px`} as CSSProperties) : style}
    >
      {capped}
    </div>
  );
  // The size container spans the pane (no padding), so breakouts read its width.
  return breakout ? <div className={styles.breakoutRoot}>{insetArea}</div> : insetArea;
};

/**
 * For an element inside a `MaxWidth inset breakout` area: grows its frame left
 * by the inset's lean (none without `lean`) and right to the pane's gutter, up
 * to the wide cap. Its rows add `var(--dsys-breakout-inset, 0px)` to their left
 * padding to keep their content on the column's left edge (fills still span the
 * frame); the extra width goes to the right. A class, so toggling it never
 * remounts.
 */
export const breakoutWideClassName = styles.breakoutWide;

/**
 * A sideways-scrolling row (a strip of cards) that spans the pane: it starts on
 * the same left edge as a `MaxWidth inset` of the same `width`, and scrolled
 * items run out across the gutter to the pane's edge. Place it directly in the
 * pane (not inside a `MaxWidth inset`), so its offset is computed from the pane.
 */
export const InsetScroll = forwardRef<
  HTMLDivElement,
  {
    width?: 'reading' | 'wide';
    // Matches a `MaxWidth inset lean` area's left edge.
    lean?: boolean;
    onScroll?: UIEventHandler<HTMLDivElement>;
    style?: CSSProperties;
    children: ReactNode;
  }
>(({width = 'reading', lean, onScroll, style, children}, ref) => (
  <div
    ref={ref}
    className={cx(
      styles.pageInset,
      width === 'wide' && styles.pageInsetWide,
      lean && styles.pageInsetLean,
      styles.insetScroll
    )}
    onScroll={onScroll}
    style={style}
  >
    {children}
  </div>
));
InsetScroll.displayName = 'InsetScroll';
